import { ShimmerThumbnail, ShimmerTitle } from 'react-shimmer-effects';
import styles from './KeyOfficialsCreditSummary.module.scss';
import { useAppSelector } from '../../../../../../app/hooks';
import SideDrawer from '../../../../../../common/_custom/SideDrawer/SideDrawer';
import DirectorHoverCardTooltip from '../../../RelatedParties/components/DirectorHoverCard/DirectorHoverCardTooltip';
import type { DirectorHoverProfile } from '../../../RelatedParties/components/DirectorHoverCard/DirectorHoverCard.types';
import { useCallback, useEffect, useState } from 'react';
import UploadData from '../../../../../Company/UploadData/UploadData';
import { USER_TYPE } from '../../../../../../utils/constants/user';
import { toast } from 'react-toastify';
import { COMBINED_REPORT, DETAILED_REPORT } from '../../CreditBureau';
import clsx from 'clsx';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import {
    INVESTOR_CB_ADD_OFFICIAL,
    INVESTOR_CB_REFRESH_BUREAU,
    INVESTOR_CB_OPEN_COMBINED_REPORT,
    INVESTOR_CB_OPEN_FULL_REPORT,
    INVESTOR_DOWNLOAD_CIBIL_QUERY,
    INVESTOR_VIEW_BUREAU_QUERY,
    AICA_CREDIT_ANALYSIS_REPORT,
} from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import {
    downloadCibilNeedInfoSheet,
    downloadTransUnionBundle,
    fetchCibilNeedInfoSheetForView,
} from '../../../../../../store/dashboard/creditBureau/service';
import FileViewerDrawer from '../../../../../../common/DocumentViewer/FileViewerDrawer';
import { EXT_MIME } from '../../../../../../common/DocumentViewer/constants';
import { capitalizeWords, isLenderType, normalizeNameKey } from '../../../../../../utils/utils';
import { formatDesignationWithShareholding, TINY_VALUE_FLOOR } from '../../../../../../utils/formatShareholdingPercent';
import { useFocusRowFromUrlParam } from '../../../../../../utils/customHooks';
import { ctaPurpleRight, creditBureauEmptyState, changeCircleBlue } from '../../../../../../assets/hostedassets';
import CtaPill from '../../../../../../common/_custom/CtaPill/CtaPill';
import {
    COMBINED_ANALYSIS_LABEL,
    EXCEL_ANALYSIS_LABEL,
    EXCEL_ANALYSIS_LOADING_LABEL,
} from '../../../../../../common/_custom/CtaPill/ctaPillConstants';
import { useCreditAnalysisReport } from '../Report/useCreditAnalysisReport';
import CreditProgessBar from './Components/CreditProgessBar';
import { UnifiedBureauReportRow } from './UnifiedBureauReportRow';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
    fetchCibilOverview,
    fetchDirectorBureauCibil,
    fetchDirectorsOverview,
} from '../../../../../../store/dashboard/creditBureau/actions';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CategorySelector from '../../../../../../common/CategorySelector/CategorySelector';
import { updateQueryParams } from '../../../../../../utils/searchParamUtils';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { infoToolTip, userBlack } from '../../../../../../assets/hostedassets';
import { BureauType, NEW_TO_CREDIT_THRESHOLD, REFRESH_BUREAU_CTA, hasBureauQueryCta, hasWorkbook } from '../Report/CreditBureauConstants';
import AicaOverview from '../../../../../../common/AicaOverview/AicaOverview';
import AddDirectorModal from '../../../../../../common/AddDirectorModal/AddDirectorModal';
import type { CreatedDirector } from '../../../../../../common/AddDirectorModal/types';
import { useAddDirectorEnrichmentHold } from './useAddDirectorEnrichmentHold';
import { ADD_DIRECTOR_CTA, ADD_DIRECTOR_PROMPT } from '../../../../../../utils/constants/commonConstants';

dayjs.extend(relativeTime);

interface TabsToDisplay {
    name: string;
    displayName: string;
    isDefault: boolean;
}

const FIELD_LABELS: Record<string, string> = { pan: 'PAN', phoneNo: 'Phone Number' };

// Read by useFocusRowFromUrlParam below and written by this tab's own post-add focus — one name
// for both, so a deep link in and a locally-published focus can't drift apart.
export const FOCUS_DIRECTOR_NAME_PARAM = 'directorName';

const getBureauErrorMessage = (bureau: string, creditBureauConfig: any): string => {
    const dataToDisplay = creditBureauConfig?.tabsToDisplay?.[bureau]?.dataToDisplay ?? {};
    const editableFields = Object.entries(dataToDisplay)
        .filter(([, cfg]: [string, any]) => cfg?.isEditable)
        .map(([key]) => FIELD_LABELS[key] ?? key);
    const fieldList = editableFields.length ? editableFields.join(' and ') : 'inputs';
    return `Consumer not found for given ${fieldList}`;
};

function KeyOfficialsCreditSummary({
    showReport,
    listDirectorsOverview,
    groupedDirectors = [] as any[],
    setSelectedReport,
    isLoading,
    assesseeOrgId,
    orgName,
    selectedCompany,
    creditBureauConfig,
    consentByDirectorId = {},
    isLatestConsentsLoaded = false,
}) {
    const [showUploader, setShowUploader] = useState(false);
    const [addDirectorOpen, setAddDirectorOpen] = useState(false);
    const [downloadingCibilQuery, setDownloadingCibilQuery] = useState(false);
    const [bureauViewerState, setBureauViewerState] = useState<{ url: string; blob: Blob } | null>(null);
    // Set only by this modal's own save, so the hold is scoped to the director it added: a modal
    // reopened while an earlier enrichment is still running is neither shown its spinner nor
    // closed under the user mid-typing.
    const [addDirectorAwaitingEnrichment, setAddDirectorAwaitingEnrichment] = useState(false);
    // The all-directors combined WORKBOOK (RECUR-28014, report B). Named
    // *Excel* throughout to keep it distinct from openCombinedReport below,
    // which navigates to AICA's combined report *view* — different thing.
    const {
        generatingReport: generatingCombinedExcel,
        reportDoc: combinedExcelDoc,
        showFile: showCombinedExcel,
        fileUrl: combinedExcelUrl,
        fileMimeType: combinedExcelMimeType,
        downloadingFile: downloadingCombinedExcel,
        openReport: openCombinedExcel,
        closeReport: closeCombinedExcel,
        downloadReport: downloadCombinedExcel,
    } = useCreditAnalysisReport();
    const [downloadingFromDrawer, setDownloadingFromDrawer] = useState(false);
    const user = useAppSelector((state) => state.user);
    // Combined bureau score for the Unified row (same source as the Combined Report).
    const cibilOverviewData = useAppSelector((state) => state.dashboard.creditBureau.cibilOverviewData);
    const isLender = isLenderType();

    // The listing's own path back into the bureau-fetch drawer, kept separate from Add.
    const openBureauDrawerForRefresh = () => {
        Mixpanel.track(INVESTOR_CB_REFRESH_BUREAU, { surface: 'PNO_LISTING' });
        setShowUploader(true);
    };
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedBureauType = searchParams.get('bureauType');
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedBureau, setSelectedBureau] = useState('');
    // Combined score stamped with the bureau it belongs to. cibilOverviewData is a shared,
    // un-stamped slice, so we track {bureau, score} and only render it when bureau matches the
    // selected tab — otherwise a tab switch would flash the previous bureau's score. null until
    // the current bureau's fetch resolves (drives the shimmer).
    const [combined, setCombined] = useState<{ bureau: string; min: number | null; max: number | null } | null>(null);

    // Row shows only when the selected bureau has a real score. Check cibilScore, not just
    // directorId — a director keeps a directorId on every bureau tab even when unfetched.
    const hasFetchedDirectorData =
        Array.isArray(listDirectorsOverview) &&
        listDirectorsOverview.some((director) => director.directorId && director.cibilScore != null);

    const isNewToCredit = (cibilScore: number | undefined): boolean => {
        return cibilScore !== undefined && cibilScore < NEW_TO_CREDIT_THRESHOLD;
    };

    /**
     * Has this director been fetched for the bureau currently selected?
     *
     * `directorId` alone is NOT that answer — it is set the first time the person
     * is fetched on ANY bureau, so a director with only an Equifax pull looked
     * "fetched" on the CIBIL tab too and rendered an empty data row (-/900, and a
     * bogus "a few seconds ago" from dayjs(undefined)) instead of the Fetch Now
     * CTA. `creditBureau` is only present on a row that carries that bureau's
     * data — polus uses the same signal to sort bureau rows ahead of
     * management-only ones. RECUR-28226.
     */
    const hasDataForSelectedBureau = (d: any): boolean => !!d?.directorId && !!d?.creditBureau;

    useFocusRowFromUrlParam({
        ready: !isLoading && Array.isArray(listDirectorsOverview) && listDirectorsOverview.length > 0,
        paramKey: FOCUS_DIRECTOR_NAME_PARAM,
        dataAttr: 'cb-director',
        fallbackParamKey: 'focusDirectorId',
        fallbackDataAttr: 'cb-directorid',
    });

    const getBureauTypes = (creditBureauConfig) =>
        Object.values(creditBureauConfig?.tabsToDisplay)
            .filter((config: any) => config.isActive)
            .map((item: any) => ({
                id: item?.name,
                label: item?.displayName,
                isDefault: item.isDefault,
                logoUrl: item.logoUrl,
            })) || [];

    const BUREAU_TYPES = getBureauTypes(creditBureauConfig);
    const defaultBureauObj = BUREAU_TYPES.find((item: any) => item.isDefault);

    useEffect(() => {
        const bureauType = searchParams.get('bureauType');
        // If 'bureauType' is null, undefined, or an empty string, set it to 'All'
        if (!bureauType) {
            const defaultBureau = defaultBureauObj?.id;

            setSelectedBureau(defaultBureau);
            searchParams.set('bureauType', defaultBureau);
        } else {
            setSelectedBureau(bureauType);
        }
        // Update query params
        updateQueryParams(searchParams, setSearchParams, {}, location);
    }, [searchParams]);

    // Fetch the selected bureau's combined score (only when it has data). Depends on the director
    // list itself (new reference on every re-fetch) so the combined score refreshes whenever
    // directors do — e.g. after the bureau drawer closes and re-fetches. reportId is included so a
    // report switch on the same org refetches. On failure we stamp a null score for that bureau so
    // the row falls back to the CTA instead of shimmering forever.
    useEffect(() => {
        if (!selectedBureauType || selectedBureauType === BureauType.ALL || !hasFetchedDirectorData) return;
        const bureau = selectedBureauType;
        dispatch(
            fetchCibilOverview(
                {
                    assesseeOrgId: selectedCompany?.assesseeOrgId,
                    reportId: selectedCompany?.reportId || '',
                    type: bureau,
                },
                { onError: () => setCombined({ bureau, min: null, max: null }) },
            ),
        );
    }, [selectedBureauType, selectedCompany?.assesseeOrgId, selectedCompany?.reportId, listDirectorsOverview]);

    // Stamp the fetched min/max score with the current bureau once it lands (takeLatest ⇒ the data
    // is always the latest bureau's). Guard on cibilOverviewData being present so the initial/empty
    // state stays "loading" (shimmer) rather than a premature CTA.
    useEffect(() => {
        if (!cibilOverviewData || !selectedBureauType || selectedBureauType === BureauType.ALL) return;
        setCombined({
            bureau: selectedBureauType,
            min: cibilOverviewData.creditScoreMin ?? null,
            max: cibilOverviewData.creditScoreMax ?? null,
        });
    }, [cibilOverviewData]);

    // Re-fetch the director list for whichever bureau tab is selected — shared by the bureau-fetch
    // drawer's own close/onSuccess handlers (unchanged) and the Add-Director modal's onSuccess (D8).
    // The `=== 'CIBIL'` check below is unreachable: the CIBIL tab sets BureauType.TRANSUNION, never
    // 'CIBIL' (see CreditBureauConstants.ts). It is kept deliberately — fetchDirectorsOverview is
    // what merges added directors + applies the RECUR-26356 designation filter, so "fixing" the
    // comparison would route CIBIL to fetchDirectorBureauCibil and silently break both. A real fix
    // requires changing the polus route first.
    const refreshDirectorsList = () => {
        const payload = {
            orgId: selectedCompany.assesseeOrgId,
            reportId: selectedCompany.reportId || '',
            type: selectedBureauType && selectedBureauType !== BureauType.ALL ? selectedBureauType : null,
        };
        if (selectedBureauType === 'CIBIL') {
            dispatch(fetchDirectorBureauCibil(payload));
        } else {
            dispatch(fetchDirectorsOverview(payload));
        }
    };

    // The rows carry no managementProfileId, and a hand-added director has no bureau directorId
    // either, so the enriched name is the only handle back to the row that was just added.
    const handleEnrichmentSettled = (enrichedName?: string) => {
        refreshDirectorsList();
        if (!enrichedName) return;
        const next = new URLSearchParams(searchParams);
        next.set(FOCUS_DIRECTOR_NAME_PARAM, normalizeNameKey(enrichedName));
        setSearchParams(next, { replace: true });
    };

    const { awaitingEnrichment, armEnrichmentHold } = useAddDirectorEnrichmentHold(assesseeOrgId, handleEnrichmentSettled);

    const closeAddDirector = useCallback(() => {
        setAddDirectorAwaitingEnrichment(false);
        setAddDirectorOpen(false);
    }, []);

    const handleAddDirectorSuccess = useCallback(
        ({ managementProfileId, pan }: CreatedDirector) => {
            const armed = armEnrichmentHold(managementProfileId, pan);
            setAddDirectorAwaitingEnrichment(armed);
            /*
               Refetching the list is what unmounts this modal behind the shimmer, so while the
               hold runs it is the hold's job to do it, once, on settle.
            */
            if (!armed) {
                setAddDirectorOpen(false);
                refreshDirectorsList();
            }
        },
        [armEnrichmentHold, refreshDirectorsList],
    );

    // Close when the hold clears, whether the enrichment landed or the poll gave up.
    useEffect(() => {
        if (addDirectorAwaitingEnrichment && !awaitingEnrichment) closeAddDirector();
    }, [addDirectorAwaitingEnrichment, awaitingEnrichment, closeAddDirector]);

    if (isLoading)
        return (
            <div className={styles.KeyOfficialsCreditSummary} style={{ padding: '12px 24px' }}>
                <ShimmerTitle line={1} gap={20} variant="primary" />
                <ShimmerThumbnail height={320} rounded className={styles.ShimmerThumbnail} />
            </div>
        );

    function getImageSource(director) {
        if (!director?.directorId) return undefined;
        return creditBureauConfig?.tabsToDisplay[director.creditBureau]?.logoUrl;
    }

    const changeSelectedCategory = (categoryId) => {
        setSelectedBureau(categoryId);
        updateQueryParams(searchParams, setSearchParams, { bureauType: categoryId }, location);
    };

    const getImageStyle = (creditBureauType) => {
        if (!creditBureauType) return undefined;
        return creditBureauConfig?.tabsToDisplay[creditBureauType]?.styles?.logoStyles;
    };

    // Selected-bureau logo for the Unified row (selectedBureauType can be null).
    const selectedBureauLogoUrl = selectedBureauType ? creditBureauConfig?.tabsToDisplay?.[selectedBureauType]?.logoUrl : undefined;

    // Trust the score only when it belongs to the selected bureau; otherwise it's still loading.
    const combinedForBureau = combined && combined.bureau === selectedBureauType ? combined : null;

    const openCombinedReport = () => {
        Mixpanel.track(INVESTOR_CB_OPEN_COMBINED_REPORT);
        showReport(COMBINED_REPORT);
    };

    const buildBureauPayload = () => {
        const bureauType = searchParams.get('bureauType') ?? BureauType.ALL;
        return {
            bureauType,
            payLoad: {
                assesseeId: assesseeOrgId,
                requestId: Date.now(),
                name: orgName,
                ...(bureauType !== BureauType.ALL && { type: bureauType }),
            },
        };
    };

    // The combined workbook is keyed on the org and the Lambda enumerates the
    // directors itself, so with none on file it can only fail. Same predicate
    // handleViewBureauQuery uses to reject that state, applied before the CTA
    // renders rather than after the click.
    const hasBureauDirectors = Array.isArray(listDirectorsOverview) && listDirectorsOverview.some((director: any) => director.directorId);

    const handleViewBureauQuery = async () => {
        if (downloadingCibilQuery) return;
        setDownloadingCibilQuery(true);
        const { bureauType, payLoad } = buildBureauPayload();
        Mixpanel.track(INVESTOR_VIEW_BUREAU_QUERY, {
            userEmail: user?.userData?.primaryEmail,
            bureauType,
        });
        if (!Array.isArray(listDirectorsOverview) || !listDirectorsOverview.some((director) => director.directorId)) {
            setDownloadingCibilQuery(false);
            toast.error('Please add officials by uploading Director CIBIL in Add New Officials');
            return;
        }
        try {
            const arrayBuffer = await fetchCibilNeedInfoSheetForView(payLoad);
            const blob = new Blob([arrayBuffer], { type: EXT_MIME.xlsx });
            const url = URL.createObjectURL(blob);
            setBureauViewerState({ url, blob });
        } catch {
            toast.error('Unable to load file. Please try again.');
        } finally {
            setDownloadingCibilQuery(false);
        }
    };

    const handleDownloadFromBureauDrawer = async () => {
        const { bureauType, payLoad } = buildBureauPayload();
        setDownloadingFromDrawer(true);
        try {
            if (bureauType === BureauType.TRANSUNION) {
                await downloadTransUnionBundle(assesseeOrgId, orgName);
            } else {
                await downloadCibilNeedInfoSheet(payLoad);
            }
        } finally {
            setDownloadingFromDrawer(false);
        }
    };

    const closeBureauViewer = () => {
        if (bureauViewerState?.url) {
            URL.revokeObjectURL(bureauViewerState.url);
        }
        setBureauViewerState(null);
    };

    return (
        <>
            <div className={styles.KeyOfficialsCreditSummary}>
                <div className={styles.Content}>
                    <div className={styles.MainHeader}>
                        <div className={styles.LeftHeader}>
                            <div className={clsx(styles.Header, { [styles.withDivider]: BUREAU_TYPES?.length > 1 })}>Director Bureau</div>
                            {BUREAU_TYPES?.length > 1 && (
                                <div className={styles.CategorySelector}>
                                    <CategorySelector
                                        categoryOptions={BUREAU_TYPES}
                                        selectedCategory={selectedBureau}
                                        changeSelectedTab={changeSelectedCategory}
                                    />
                                </div>
                            )}
                        </div>
                        <div className={styles.BarRow}>
                            {/* <Button
                                variant="purpleinverted"
                                onClick={() => {
                                    Mixpanel.track(INVESTOR_CB_OPEN_COMBINED_REPORT);
                                    if (
                                        Array.isArray(listDirectorsOverview) &&
                                        listDirectorsOverview?.length > 0
                                    ) {
                                        showReport(COMBINED_REPORT);
                                    } else
                                        toast.error(
                                            'Please add officials by uploading Director CIBIL in Add New Officials',
                                        );
                                }}
                                text="Combined Director Report"
                                fit
                                style={{ marginTop: '0', fontWeight: '700' }}
                                size="small"
                            /> */}

                            {/* <Button
                                variant="purpleinverted"
                                onClick={async () => {
                                    setDownloadingCibilQuery(true);
                                    Mixpanel.track(INVESTOR_DOWNLOAD_CIBIL_QUERY);
                                    if (
                                        Array.isArray(listDirectorsOverview) &&
                                        listDirectorsOverview?.length > 0
                                    ) {
                                        const payLoad = {
                                            assesseeId: assesseeOrgId,
                                            requestId: Date.now(),
                                            name: orgName,
                                        };
                                        await downloadCibilNeedInfoSheet(payLoad);
                                        setDownloadingCibilQuery(false);
                                    } else {
                                        setDownloadingCibilQuery(false);
                                        toast.error(
                                            'Please add officials by uploading Director CIBIL in Add New Officials',
                                        );
                                    }
                                }}
                                text="Download Bureau Query"
                                fit
                                style={{ marginTop: '0', fontWeight: '700' }}
                                size="small"
                                loading={downloadingCibilQuery}
                            /> */}
                            <div className={styles.HeaderButtons}>
                                {/* Combined Credit Analysis across every director of the org
                                    (RECUR-28014, report B). Gated on hasWorkbook rather than a
                                    bureau comparison: each bureau needs its own renderer, because
                                    grading one bureau's file under another's status vocabulary
                                    reads as a clean payment history instead of an error. */}
                                {hasWorkbook(selectedBureauType) && hasBureauDirectors && (
                                    <CtaPill
                                        tone="blue"
                                        icon={changeCircleBlue}
                                        label={COMBINED_ANALYSIS_LABEL}
                                        loadingLabel={EXCEL_ANALYSIS_LOADING_LABEL}
                                        loading={generatingCombinedExcel}
                                        onClick={() => {
                                            Mixpanel.track(AICA_CREDIT_ANALYSIS_REPORT, {
                                                scope: 'COMBINED',
                                                bureau: selectedBureauType,
                                                source: 'KeyOfficials',
                                            });
                                            openCombinedExcel(assesseeOrgId, null, selectedBureauType, 'COMBINED');
                                        }}
                                    />
                                )}
                                {/* The Bureau Query (need-info sheet) CTA, for a bureau with no
                                    workbook renderer yet — the Combined Analysis workbook above
                                    supersedes it wherever one exists, and the ALL tab is not
                                    bureau-specific. The truthy guard is load-bearing:
                                    selectedBureauType is null until a bureau is chosen, and both
                                    `null !== ALL` and `!hasWorkbook(null)` hold, so without it this
                                    CTA rendered before selection and posted the ALL payload with no
                                    type. With that excluded and all three bureaus now carrying a
                                    renderer, the branch cannot render at all; it is kept as the
                                    guard for the next bureau added to BureauType before its
                                    renderer lands. See __tests__/workbookBureaus.test.ts. */}
                                {hasBureauQueryCta(selectedBureauType) && (
                                    <CtaPill
                                        tone="blue"
                                        icon={changeCircleBlue}
                                        label={EXCEL_ANALYSIS_LABEL}
                                        loadingLabel={EXCEL_ANALYSIS_LOADING_LABEL}
                                        loading={downloadingCibilQuery}
                                        onClick={handleViewBureauQuery}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={styles.cbBody}>
                        <div className={styles.OverviewContainer}>
                            <div className={styles.Overview}>
                                <div className={styles.Headers}>
                                    <div style={{ paddingBottom: '1.188rem' }}>Name</div>
                                    <div style={{ paddingBottom: '1.188rem' }}>Bureau Score</div>
                                    <div style={{ paddingBottom: '1.188rem' }}>DPD Accounts</div>
                                    <div style={{ paddingBottom: '1.188rem' }}>Overdue</div>
                                    <div style={{ display: 'flex', paddingBottom: '16px' }}>
                                        Enquiries (90D)
                                        <Tooltip
                                            content={'(Enquiries in last 90 days)'}
                                            direction="right"
                                            style={{ top: '100%', width: 'fit-content' }}
                                        >
                                            <img src={infoToolTip} height={'16px'} style={{ marginLeft: '4px', cursor: 'pointer' }} />
                                        </Tooltip>
                                    </div>
                                    <div style={{ paddingBottom: '1.188rem' }}>Max DPD</div>
                                    <div style={{ paddingBottom: '1.188rem' }}>Fetched</div>
                                </div>

                                {(() => {
                                    // Person-grouping is done by polus (groupedDirectors); the FE only flattens the
                                    // BE-provided groups for row rendering (name/designation shown once per person).
                                    // Fallback keeps rows rendering (ungrouped) if an older BE has no groupedDirectors.
                                    const __persons =
                                        groupedDirectors && groupedDirectors.length
                                            ? groupedDirectors
                                            : (listDirectorsOverview || []).map((d: any) => ({ bureaus: [d] }));
                                    const __flat: {
                                        director: any;
                                        isFirstOfGroup: boolean;
                                        isLastOfGroup: boolean;
                                        directorProfile?: DirectorHoverProfile;
                                    }[] = [];
                                    __persons.forEach((person: any) =>
                                        (person?.bureaus || []).forEach((d: any, bi: number, arr: any[]) =>
                                            __flat.push({
                                                director: d,
                                                isFirstOfGroup: bi === 0,
                                                isLastOfGroup: bi === arr.length - 1,
                                                // The hover card's profile hangs off the PERSON group (polus attaches it
                                                // there, once), not off each bureau row — carry it down with the row that
                                                // renders the name, which is the group's first.
                                                directorProfile: person?.directorProfile,
                                            }),
                                        ),
                                    );
                                    return __flat.map(({ director, isFirstOfGroup, isLastOfGroup, directorProfile }, i) => (
                                        <div
                                            className={styles.Row}
                                            key={director.id || i}
                                            data-cb-director={normalizeNameKey(director.name)}
                                            data-cb-directorid={director.directorId ?? ''}
                                            onClick={() => {
                                                if (hasDataForSelectedBureau(director) && (!director.bureauError || director.cibilScore)) {
                                                    Mixpanel.track(INVESTOR_CB_OPEN_FULL_REPORT);
                                                    showReport(DETAILED_REPORT, director);
                                                } else if (!isLender) {
                                                    Mixpanel.track(INVESTOR_CB_ADD_OFFICIAL, { surface: 'PNO_LISTING_FETCH' });
                                                    setShowUploader(true);
                                                } else {
                                                    toast.error('Access restricted. Please ask the admin to fetch the credit details.');
                                                }
                                            }}
                                        >
                                            <div className={styles.Name}>
                                                {isFirstOfGroup && (
                                                    <div className={styles.NameRow}>
                                                        <img src={userBlack} alt="" className={styles.PersonIcon} />
                                                        <DirectorHoverCardTooltip
                                                            profile={directorProfile}
                                                            unmatched={
                                                                directorProfile
                                                                    ? undefined
                                                                    : {
                                                                          name: director.name,
                                                                          designation: director.designation,
                                                                          shareholdingPercentage: director.shareholdingPercentage,
                                                                      }
                                                            }
                                                        >
                                                            <div className={styles.NameTextBlock}>
                                                                <div>{capitalizeWords(director.name)}</div>
                                                                <div className={styles.DesignationText}>
                                                                    {(() => {
                                                                        const text = formatDesignationWithShareholding(
                                                                            director.designation,
                                                                            director.shareholdingPercentage,
                                                                            { hideWhenNull: true },
                                                                        );
                                                                        const value = director.shareholdingPercentage;
                                                                        // Below the tiny-value floor the display collapses to
                                                                        // "<0.0001%" — a tooltip with the exact raw value is the
                                                                        // only way to see what it actually is.
                                                                        const isTinyCollapsed =
                                                                            value != null &&
                                                                            Number.isFinite(value) &&
                                                                            value > 0 &&
                                                                            value < TINY_VALUE_FLOOR;
                                                                        if (!isTinyCollapsed) return text;
                                                                        return (
                                                                            <Tooltip
                                                                                content={`${value}%`}
                                                                                direction="top"
                                                                                contentCss={{ whiteSpace: 'nowrap' }}
                                                                                fitContent
                                                                                small
                                                                                usePortal
                                                                            >
                                                                                <span>{text}</span>
                                                                            </Tooltip>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        </DirectorHoverCardTooltip>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Conditionally render credit data or error message */}
                                            {hasDataForSelectedBureau(director) && (!director.bureauError || director.cibilScore) ? (
                                                <>
                                                    <div className={styles.Cibil}>
                                                        {isNewToCredit(director.cibilScore) ? (
                                                            <div className={styles.NewToCreditContainer}>
                                                                <span className={styles.NewToCreditText}>Consumer is new to Credit</span>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <img
                                                                    src={getImageSource(director)}
                                                                    alt=""
                                                                    className={styles.Image}
                                                                    style={getImageStyle(director?.creditBureau)}
                                                                />
                                                                <div>
                                                                    {director.cibilScore ?? '-'}
                                                                    <span>/900</span>
                                                                </div>
                                                                <CreditProgessBar
                                                                    width={((director.cibilScore ?? 0) / 900) * 100}
                                                                    ParentWidth="12.5rem"
                                                                />
                                                            </>
                                                        )}
                                                    </div>

                                                    <div
                                                        className={`${styles.Dot} ${director.dpdAccounts == 0 ? styles.Green : styles.Red}`}
                                                    >
                                                        {director.dpdAccounts ?? '-'}
                                                    </div>

                                                    <div
                                                        className={`${styles.Dot} ${
                                                            director.currentOverdue == 0 ? styles.Green : styles.Red
                                                        }`}
                                                    >
                                                        ₹{director.currentOverdue?.toLocaleString?.('en-IN') || '-'}
                                                    </div>
                                                    <div
                                                        className={`${styles.Dot} ${
                                                            director.enquiriesInPast90Days == 0 ? styles.Green : styles.Red
                                                        }`}
                                                    >
                                                        {director.enquiriesInPast90Days ?? '-'} enquiries
                                                    </div>
                                                    <div className={`${styles.Dot} ${director.maxDpds == 0 ? styles.Green : styles.Red}`}>
                                                        {director.maxDpds ?? '-'} days
                                                    </div>
                                                </>
                                            ) : director.bureauError ? (
                                                <div className={styles.ErrorMessageSpan}>
                                                    {getBureauErrorMessage(director.creditBureau, creditBureauConfig)}
                                                </div>
                                            ) : (
                                                <>
                                                    <div className={styles.NA}></div>
                                                    <div className={styles.NA}></div>
                                                    <div className={styles.NA}></div>
                                                    <div className={styles.NA}></div>
                                                    <div className={styles.NA}></div>
                                                </>
                                            )}

                                            <div className={styles.Fetched} style={{ marginLeft: '15px' }}>
                                                {hasDataForSelectedBureau(director) ? (
                                                    <div className={styles.Date}>{dayjs(director?.lastFetched)?.fromNow()}</div>
                                                ) : (
                                                    <div
                                                        className={styles.FetchNow}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (isLender) return;
                                                            Mixpanel.track(INVESTOR_CB_ADD_OFFICIAL, { surface: 'PNO_LISTING_FETCH' });
                                                            setShowUploader(true);
                                                        }}
                                                    >
                                                        <img src={ctaPurpleRight} alt="" />
                                                        <span className={styles.FetchNowText}>Fetch Now</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ));
                                })()}

                                {hasFetchedDirectorData && (
                                    <UnifiedBureauReportRow
                                        isAllTab={selectedBureauType === BureauType.ALL}
                                        loading={!combinedForBureau}
                                        minScore={combinedForBureau?.min}
                                        maxScore={combinedForBureau?.max}
                                        logoUrl={selectedBureauLogoUrl}
                                        logoStyle={getImageStyle(selectedBureauType)}
                                        onOpenReport={openCombinedReport}
                                    />
                                )}

                                {!isLender &&
                                    (listDirectorsOverview?.length === 0 ? (
                                        <div className={clsx(styles.AddOfficialEmptyState)}>
                                            <img src={creditBureauEmptyState} alt="No Directors" className={styles.EmptyStateImage} />
                                            <p className={styles.EmptyStateText}>
                                                {ADD_DIRECTOR_PROMPT}&nbsp;
                                                <span
                                                    className={styles.Add}
                                                    onClick={() => {
                                                        Mixpanel.track(INVESTOR_CB_ADD_OFFICIAL, { surface: 'PNO_LISTING_ADD' });
                                                        setAddDirectorOpen(true);
                                                    }}
                                                >
                                                    {ADD_DIRECTOR_CTA}
                                                </span>
                                            </p>
                                        </div>
                                    ) : (
                                        <div className={clsx(styles.AddOfficial)}>
                                            {ADD_DIRECTOR_PROMPT}&nbsp;
                                            <span
                                                className={styles.Add}
                                                onClick={() => {
                                                    Mixpanel.track(INVESTOR_CB_ADD_OFFICIAL, { surface: 'PNO_LISTING_ADD' });
                                                    setAddDirectorOpen(true);
                                                }}
                                            >
                                                {ADD_DIRECTOR_CTA}
                                            </span>
                                            <span className={styles.FooterDivider} />
                                            <span
                                                className={styles.RefreshBureau}
                                                role="button"
                                                tabIndex={0}
                                                onClick={openBureauDrawerForRefresh}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        openBureauDrawerForRefresh();
                                                    }
                                                }}
                                            >
                                                {REFRESH_BUREAU_CTA}
                                            </span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                </div>
                {showUploader && (
                    <SideDrawer
                        onClose={() => {
                            setShowUploader(false);
                            refreshDirectorsList();
                        }}
                        width="60%"
                    >
                        <SideDrawer.Header>
                            Bureau Details for Directors
                            <span className={styles.Grey}> | {selectedCompany?.orgName}</span>
                        </SideDrawer.Header>
                        <UploadData
                            overrideSelectedStep={'creditBureau'}
                            userType={USER_TYPE.INVESTOR}
                            isInvestor
                            assesseeOrgId={assesseeOrgId}
                            closeDrawer={() => {
                                refreshDirectorsList();
                                setShowUploader(false);
                            }}
                        />
                        {/* <SideDrawer.Footer>
                            <div className={styles.FooterText}>
                                <img src={greyLock} alt="lock" height="10px" />
                                256-Bit Bank-Level Encryption
                            </div>
                        </SideDrawer.Footer> */}
                    </SideDrawer>
                )}
                {!!bureauViewerState && (
                    <FileViewerDrawer
                        heading={`Bureau Query — ${orgName ?? 'Report'}`}
                        fileUrl={bureauViewerState.url}
                        mimeType={EXT_MIME.xlsx}
                        downloading={downloadingFromDrawer}
                        downloadLabel="Download Bureau Query"
                        onDownload={handleDownloadFromBureauDrawer}
                        onClose={closeBureauViewer}
                    />
                )}
                {showCombinedExcel && (
                    <FileViewerDrawer
                        heading={combinedExcelDoc?.name ?? 'Combined Director Report'}
                        fileUrl={combinedExcelUrl}
                        mimeType={combinedExcelMimeType}
                        downloading={downloadingCombinedExcel}
                        onDownload={downloadCombinedExcel}
                        onClose={closeCombinedExcel}
                    />
                )}
                <AddDirectorModal
                    open={addDirectorOpen}
                    onClose={closeAddDirector}
                    panProfileFetching={addDirectorAwaitingEnrichment}
                    onSuccess={handleAddDirectorSuccess}
                    organizationId={assesseeOrgId}
                />
            </div>
            {/* <div
                style={{ background: 'transparent', marginTop: '-1rem', padding: 0 }}
                className={styles.KeyOfficialsCreditSummary}
            >
                <ChargesTable />
            </div> */}
        </>
    );
}

export default KeyOfficialsCreditSummary;
